import {
    ADDITIONAL_EMAILS_RESOURCE_NAME,
    MAX_CHATS_RESOURCE_NAME,
    TELEGRAM_CHATS_RESOURCE_NAME,
} from 'shared/config/model/urls';
import {AUTO_SELECT_ID} from 'shared/lib/constants';

import {t} from '@lingui/core/macro';

import {IUserOutput} from 'entities/user';
import CreatePortMonitorType from 'features/port/model/PortMonitor/CreatePortMonitorType';
import {idsToIri} from 'shared/api/iriHelper';
import {normalizeTags} from 'entities/monitor';

export type PortBatchEditFormValues = Omit<CreatePortMonitorType, 'ip' | 'port' | 'name'>;

export type PortBatchEditFieldKey =
    | 'desired_state'
    | 'check_interval'
    | 'timeout'
    | 'locations'
    | 'threshold'
    | 'tags'
    | 'notify';

export interface IPortBatchEditField {
    key: PortBatchEditFieldKey;
    label: () => string;
    formKeys: (keyof PortBatchEditFormValues)[];
    isAvailable: (_user: IUserOutput | null) => boolean;
}

export const PORT_BATCH_EDIT_FIELDS: IPortBatchEditField[] = [
    {
        key: 'desired_state',
        label: () => t`Желаемое состояние`,
        formKeys: ['desired_state'],
        isAvailable: () => true,
    },
    {
        key: 'check_interval',
        label: () => t`Интервал проверки`,
        formKeys: ['check_interval'],
        isAvailable: () => true,
    },
    {
        key: 'timeout',
        label: () => t`Таймаут проверки`,
        formKeys: ['timeout'],
        isAvailable: () => true,
    },
    {
        key: 'locations',
        label: () => t`Локации мониторинга`,
        formKeys: ['locations'],
        isAvailable: () => true,
    },
    {
        key: 'threshold',
        label: () => t`Порог срабатывания`,
        formKeys: ['threshold'],
        isAvailable: () => true,
    },
    {
        key: 'tags',
        label: () => t`Теги`,
        formKeys: ['tags'],
        isAvailable: () => true,
    },
    {
        key: 'notify',
        label: () => t`Уведомления`,
        formKeys: ['notify_by_email', 'additional_emails', 'telegram_chats', 'max_chats'],
        isAvailable: () => true,
    },
];

export const PORT_BATCH_EDIT_FIELD_BY_KEY: Record<string, IPortBatchEditField> = PORT_BATCH_EDIT_FIELDS
    .reduce(
        (acc, field) => {
            acc[field.key] = field;
            return acc;
        },
        {} as Record<string, IPortBatchEditField>,
    );

export function validatePortBatchEditFields(addedFields: string[]): string | undefined {
    const hasLocations = addedFields.includes('locations');
    const hasThreshold = addedFields.includes('threshold');

    return hasLocations === hasThreshold
        ? undefined
        : t`Локации и порог проверки можно изменять только вместе`;
}

export interface IPortBatchEditPayload {
    data?: Record<string, unknown>;
    error?: string;
}

const IRI_RESOURCE: Partial<Record<keyof PortBatchEditFormValues, string>> = {
    telegram_chats: TELEGRAM_CHATS_RESOURCE_NAME,
    max_chats: MAX_CHATS_RESOURCE_NAME,
    additional_emails: ADDITIONAL_EMAILS_RESOURCE_NAME,
};

export function buildPortBatchEditPayload(
    addedFields: string[],
    formData: PortBatchEditFormValues,
    selectedIds: string[],
): IPortBatchEditPayload {
    const error = validatePortBatchEditFields(addedFields);

    if (error) {
        return {error};
    }

    const data: Record<string, unknown> = {ids: selectedIds};

    const activeFormKeys = new Set<keyof PortBatchEditFormValues>();
    addedFields.forEach(key => {
        const field = PORT_BATCH_EDIT_FIELD_BY_KEY[key];
        if (field) {
            field.formKeys.forEach(formKey => activeFormKeys.add(formKey));
        }
    });

    const selectedLocations = (formData.locations ?? []).filter(id => id !== AUTO_SELECT_ID);

    activeFormKeys.forEach(formKey => {
        if (formKey === 'locations') {
            data.locations = idsToIri(selectedLocations, 'locations');
            return;
        }

        if (formKey === 'threshold') {
            data.threshold = selectedLocations.length > 0 ? formData.threshold : 1;
            return;
        }

        if (formKey === 'tags') {
            data.tags = normalizeTags(formData.tags ?? []);
            return;
        }

        const resource = IRI_RESOURCE[formKey];
        if (resource) {
            data[formKey] = idsToIri((formData[formKey] as unknown) as string[], resource);
            return;
        }

        data[formKey] = formData[formKey];
    });

    return {data};
}
